import type { PublicApiClient } from '../api/client';
import type { AccountMessageKey } from '../i18n/account';

export type AccountHandleSuggestionTranslator = {
  t(key: AccountMessageKey): string;
};

export type AccountHandleAvailability = {
  ok: boolean;
  available: boolean;
  handle?: string;
  handleNormalized?: string;
  suggestions?: string[];
};

export async function checkAccountHandleAvailability(
  api: PublicApiClient,
  pseudonym: string,
  options: { suggest?: boolean; limit?: number; signal?: AbortSignal } = {},
): Promise<AccountHandleAvailability> {
  const params = new URLSearchParams();
  params.set('pseudonym', pseudonym);
  if (options.suggest !== false) params.set('suggest', '1');
  if (options.limit) params.set('limit', String(options.limit));
  return api.request<AccountHandleAvailability>(`/api/account/check-handle?${params.toString()}`, {
    signal: options.signal,
  });
}

export async function ensureAccountHandleAvailable(
  api: PublicApiClient,
  input: HTMLInputElement | null,
  translator: AccountHandleSuggestionTranslator,
  signal?: AbortSignal,
): Promise<boolean> {
  if (!input) return true;
  const value = input.value.trim();
  if (!value) {
    clearAccountHandleSuggestions(input);
    return true;
  }
  try {
    const result = await checkAccountHandleAvailability(api, value, {
      suggest: true,
      limit: 3,
      signal,
    });
    if (result.available) {
      input.setCustomValidity('');
      clearAccountHandleSuggestions(input);
      return true;
    }
    input.setCustomValidity(translator.t('handleTaken'));
    renderAccountHandleSuggestions(input, result.suggestions || [], translator);
    return false;
  } catch {
    return true;
  }
}

export function renderAccountHandleSuggestions(
  input: HTMLInputElement,
  suggestions: string[],
  translator: AccountHandleSuggestionTranslator,
): void {
  const field =
    input.closest<HTMLElement>('[data-auth-field]') || input.closest<HTMLElement>('label');
  if (!field?.parentElement) return;
  clearAccountHandleSuggestions(input);
  const unique = Array.from(new Set(suggestions.map((item) => item.trim()).filter(Boolean))).slice(
    0,
    5,
  );
  if (!unique.length) return;

  const box = document.createElement('div');
  box.className = 'qe-auth-handle-suggestions';
  box.setAttribute('data-account-handle-suggestions', '');
  box.innerHTML = [
    `<p class="qe-auth-handle-suggestions__text">${escapeHtml(translator.t('handleTaken'))} ${escapeHtml(translator.t('pseudonymSuggestions'))}</p>`,
    `<div class="qe-auth-handle-suggestions__chips">${unique
      .map(
        (item) =>
          `<button class="qe-auth-handle-suggestions__chip" type="button" data-account-handle-suggestion="${escapeAttr(item)}">${escapeHtml(item)}</button>`,
      )
      .join('')}</div>`,
  ].join('');
  field.insertAdjacentElement('afterend', box);
  box.querySelectorAll<HTMLButtonElement>('[data-account-handle-suggestion]').forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.accountHandleSuggestion || button.textContent || '';
      input.value = value;
      input.setCustomValidity('');
      clearAccountHandleSuggestions(input);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.focus();
    });
  });
}

export function clearAccountHandleSuggestions(input: HTMLInputElement | null): void {
  const field =
    input?.closest<HTMLElement>('[data-auth-field]') || input?.closest<HTMLElement>('label');
  const sibling = field?.nextElementSibling;
  if (sibling instanceof HTMLElement && sibling.hasAttribute('data-account-handle-suggestions')) {
    sibling.remove();
  }
}

function escapeHtml(value: string): string {
  return String(value).replace(
    /[&<>'"]/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;',
      })[char] || char,
  );
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}
